/**
 * Gate 1 — page shell.
 *
 * Owns the surrounding chrome (tabs bar, breadcrumb, hero header) and the
 * Step unlock chain: Step 1 ready → Step 2 Risk Register → Step 3 Metrics.
 * Each step is self-contained — fetching, state, and validation live in their
 * own component. Crucially, this page does NOT prefetch Step 2/3 until the
 * prior step reports ready.
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, Chip, CircularProgress, Snackbar, Tooltip, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { ReactComponent as ChevronLeftIcon } from "../../asserts/logo/ChevronLeft.svg";
import { ReactComponent as RegenerateIcon } from "../../asserts/logo/RegenerateIcon.svg";
import IntakeGateTabsBar from "../../components/intake/IntakeGateTabsBar";
import IntakeBreadcrumb from "../../components/intake/IntakeBreadcrumb";
import IntakeHeaderBackButton from "../../components/intake/IntakeHeaderBackButton";
import { INTAKE_COLORS, INTAKE_FONT } from "../../theme/intakeTokens";
import { getIntakeSession, enterGate1 } from "../../services/intakeClient";
import Step1TrustAttributes from "./Step1TrustAttributes";
import Step2RiskRegister from "./Step2RiskRegister";
import Step3MetricsRegister from "./Step3MetricsRegister";

const log = (...args) => console.log("[GATE1.PAGE]", ...args);

const ACTIVE_GATE = 1;

/** Fluid page padding — grows with viewport so content fills large monitors. */
const PAGE_PX = { xs: 1.5, sm: 2, md: 3, lg: 4, xl: 5 };

/**
 * Back-arrow placement — same 24px / 16px ink standard as Gate 0 App Identity,
 * chatbot, and summary. CSS backs out the 6px hit-target inset and Lato's ~1px
 * capital side bearing. Other header gaps stay 8px.
 */
const BACK_ARROW_INK_LEFT_PX = 24;
const BACK_ARROW_INK_GAP_RIGHT_PX = 16;
const BACK_ARROW_HIT_TARGET_INSET_PX = 6;
const BACK_ARROW_BUTTON_SIZE_PX = 32;
const TITLE_LEFT_SIDE_BEARING_PX = 1;
const HEADER_INSET_LEFT_PX = BACK_ARROW_INK_LEFT_PX - BACK_ARROW_HIT_TARGET_INSET_PX;
const BACK_ARROW_GAP_RIGHT_PX =
  BACK_ARROW_INK_GAP_RIGHT_PX - BACK_ARROW_HIT_TARGET_INSET_PX - TITLE_LEFT_SIDE_BEARING_PX;
const HEADER_ROW_GAP_PX = 8;
const TITLE_NUDGE_LEFT_PX = BACK_ARROW_GAP_RIGHT_PX - HEADER_ROW_GAP_PX;
const HEADER_TEXT_COLUMN_INSET_PX = BACK_ARROW_BUTTON_SIZE_PX + BACK_ARROW_GAP_RIGHT_PX;

/** Header flag pills — Figma Canonical / Derived (no toggle yet) */
const HEADER_FLAG_BASE_SX = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 24,
  boxSizing: "border-box",
  borderRadius: "12px",
  gap: "10px",
  px: "8px",
  opacity: 1,
};

const HEADER_FLAG_LABEL_SX = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 24,
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontStyle: "Bold",
  fontSize: "13px",
  lineHeight: "24px",
  letterSpacing: "0%",
  textAlign: "center",
  whiteSpace: "nowrap",
};

const INTAKE_PAGE_SHELL = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  minWidth: 0,
  height: "100%",
  minHeight: "100%",
  bgcolor: INTAKE_COLORS.surface,
  fontFamily: INTAKE_FONT,
  overflow: "hidden",
  position: "relative",
  boxSizing: "border-box",
};

const SCROLL_AREA_SX = {
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  overflowY: "auto",
  overflowX: "hidden",
  px: PAGE_PX,
  py: { xs: 2, md: 3 },
  bgcolor: "#F5F5F5",
  boxSizing: "border-box",
};

/** Same Default / Hover / Pressed / Loading styles as Test Strategy Regener button. */
const REGEN_BG = "linear-gradient(90deg, #1756E5 0%, #8131E2 100%)";
const REGEN_BG_PRESSED = "linear-gradient(90deg, #0A2E99 0%, #521499 100%)";
const REGEN_SHADOW = "0px 2px 4px 0px rgba(38, 26, 102, 0.15)";
const REGEN_SHADOW_HOVER = "0px 6px 18px 2px rgba(89, 26, 178, 0.35)";
const REGEN_SHADOW_PRESSED = "0px 1px 1px 0px rgba(0, 0, 0, 0.05)";
const REGEN_LOADING_BG =
  "linear-gradient(90deg, #1756E5 0%, #1756E5 25%, #8C73FF 50%, #8131E2 75%, #8131E2 100%)";

const FOOTER_BTN_SHADOW = "0px 1px 2px 0px rgba(0, 0, 0, 0.04)";
const FOOTER_BTN_INSET = "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)";

const footerGhostBtnSx = {
  minWidth: 0,
  width: "fit-content",
  height: 40,
  minHeight: 40,
  px: "16px",
  py: "8px",
  gap: "4px",
  boxSizing: "border-box",
  borderRadius: "4px",
  border: "1px solid #005BF0",
  bgcolor: "#FFFFFF",
  color: "#005BF0",
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  boxShadow: FOOTER_BTN_SHADOW,
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled):not(:active)": {
    bgcolor: "#F0F6FF",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: FOOTER_BTN_SHADOW,
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#FFFFFF",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: FOOTER_BTN_INSET,
  },
  "& .MuiTouchRipple-root": { display: "none" },
};

const generateTestStrategyBtnSx = {
  "@keyframes regenShimmer": {
    "0%": { backgroundPosition: "100%" },
    "100%": { backgroundPosition: "-100%" },
  },
  "@keyframes regenSpin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  minWidth: 0,
  width: "fit-content",
  height: 40,
  minHeight: 40,
  px: "20px",
  py: "10px",
  gap: "8px",
  boxSizing: "border-box",
  borderRadius: "8px",
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 13,
  lineHeight: "20px",
  letterSpacing: 0,
  color: "#FFFFFF",
  bgcolor: "transparent",
  backgroundImage: REGEN_BG,
  border: "1px solid transparent",
  boxShadow: REGEN_SHADOW,
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": {
    m: 0,
    "& > *:nth-of-type(1)": { fontSize: 20, width: 20, height: 20 },
  },
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "transparent",
    backgroundImage: REGEN_BG,
    boxShadow: REGEN_SHADOW_HOVER,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${REGEN_BG} !important`,
    border: "2px solid #005FCC",
    boxShadow: REGEN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${REGEN_BG} !important`,
    border: "2px solid #005FCC",
    boxShadow: REGEN_SHADOW_HOVER,
  },
  "&:active:not(.Mui-disabled)": {
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${REGEN_BG_PRESSED} !important`,
    border: "1px solid transparent",
    boxShadow: REGEN_SHADOW_PRESSED,
  },
  "& .MuiTouchRipple-root": { display: "none" },
  "&.Mui-disabled": {
    color: "#BBBBBB",
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    opacity: 1,
    cursor: "not-allowed",
  },
};

function formatLastUpdated(date) {
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `Last updated ${month} ${day}, ${year} • ${hours}:${minutes}`;
}

export default function Gate1Page() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  // Step 2 is locked until Step 1 publishes `ready = true`.
  const [step1Ready, setStep1Ready] = useState(false);
  // Step 3 is locked until Step 2 has at least one active risk.
  const [step2Ready, setStep2Ready] = useState(false);
  const [risksRevision, setRisksRevision] = useState("");
  // Test Strategy unlocks once Step 3 has at least one active metric.
  const [metricsReady, setMetricsReady] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(() => new Date());
  const [toast, setToast] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    log("mounted — projectId:", projectId);
    return () => log("unmounted — projectId:", projectId);
  }, [projectId]);

  // On Gate 1 entry, clear the session's RAG + extraction caches.
  // View Summary must NOT call this.
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const sessionRes = await getIntakeSession(projectId);
        const sid = sessionRes?.data?.session_id;
        if (!sid) return;
        await enterGate1(projectId, sid);
      } catch (err) {
        log("enterGate1 cache clear failed (non-fatal):", err?.message || err);
      }
    })();
  }, [projectId]);

  const handleStep1ReadyChange = (ready) => {
    log("Step1 → ready transition:", step1Ready, "→", ready,
        ready ? "(Step 2 will now fetch)" : "(Step 2 re-locked)");
    setStep1Ready(ready);
    if (!ready) setStep2Ready(false);
    setLastUpdatedAt(new Date());
  };

  const handleRisksReadyChange = (ready, revision = "") => {
    setStep2Ready((prev) => {
      if (prev === ready) return prev;
      log("Step2 → risks ready:", ready, ready ? "(Step 3 unlocks)" : "(Step 3 locked)");
      return ready;
    });
    // Always bump revision so Step 3 refreshes newly added/edited risks
    setRisksRevision(typeof revision === "string" ? revision : "");
  };

  const lastUpdatedLabel = formatLastUpdated(lastUpdatedAt);

  const handlePageBack = () => navigate(`/readiness/${projectId}`);

  return (
    <Box sx={INTAKE_PAGE_SHELL}>
      <IntakeGateTabsBar
        activeGate={ACTIVE_GATE}
        unlockedUpTo={ACTIVE_GATE}
        onGateClick={(gateIndex) => {
          if (gateIndex === 0) navigate(`/chat/${projectId}`);
        }}
        showBack={false}
        showViewSummary={false}
      />

      <IntakeBreadcrumb activeGate={ACTIVE_GATE} />

      {/* Hero header — Figma banner frame (hug content; no empty bottom gap) */}
      <Box
        sx={{
          flexShrink: 0,
          width: "100%",
          height: "auto",
          boxSizing: "border-box",
          bgcolor: INTAKE_COLORS.surface,
          border: "1px solid #E6E6E6",
          opacity: 1,
          pl: `${HEADER_INSET_LEFT_PX}px`,
          pr: PAGE_PX,
          pt: "16px",
          pb: "8px",
        }}
      >
        {/* Content stack — tighter vertical gaps like Figma */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            width: "100%",
            opacity: 1,
          }}
        >
          {/* Row 1 — eyebrow + status chip (Figma) */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              width: "100%",
              minHeight: 24,
              pl: `${HEADER_TEXT_COLUMN_INSET_PX}px`,
              boxSizing: "border-box",
            }}
          >
            {/* Task B — eyebrow */}
            <Typography
              sx={{
                flex: "1 1 auto",
                minWidth: 0,
                maxWidth: { xs: "100%", sm: 824 },
                height: 19,
                m: 0,
                fontFamily: "Lato, sans-serif",
                fontWeight: 400,
                fontStyle: "Regular",
                fontSize: "16px",
                lineHeight: "100%",
                letterSpacing: "0px",
                color: "#383838",
                verticalAlign: "middle",
              }}
            >
              Gate 1 - Assurance Composition
            </Typography>

            {/* Right — status flag only (Figma: no Test Strategy button here) */}
            {step1Ready ? (
              <Chip
                label="Gate-1 weights ready"
                size="small"
                sx={{
                  bgcolor: "#ECFDF3",
                  color: "#067647",
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  height: 24,
                  minWidth: 80,
                  boxSizing: "border-box",
                  borderRadius: "12px",
                  gap: "10px",
                  px: "8px",
                  bgcolor: "#FFE999",
                  opacity: 1,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 64,
                    height: 24,
                    fontFamily: "Lato, sans-serif",
                    fontWeight: 700,
                    fontStyle: "Bold",
                    fontSize: "13px",
                    lineHeight: "24px",
                    letterSpacing: "0%",
                    textAlign: "center",
                    verticalAlign: "middle",
                    color: "#8A5C00",
                    whiteSpace: "nowrap",
                  }}
                >
                  In progress
                </Box>
              </Box>
            )}
          </Box>

          {/* Row 2 — title + live timestamp (Figma) */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              width: "100%",
              minHeight: 32,
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: `${HEADER_ROW_GAP_PX}px`,
                flex: "1 1 auto",
                minWidth: 0,
                maxWidth: { xs: "100%", sm: 617 },
              }}
            >
              <IntakeHeaderBackButton onClick={handlePageBack} aria-label="Back" />
              <Typography
                sx={{
                  ml: `${TITLE_NUDGE_LEFT_PX}px`,
                  flex: "1 1 auto",
                  minWidth: 0,
                  minHeight: 32,
                  m: 0,
                  fontFamily: "Lato, sans-serif",
                  fontWeight: 900,
                  fontStyle: "Black",
                  fontSize: { xs: 20, sm: 22, md: 24, xl: 26 },
                  lineHeight: "32px",
                  letterSpacing: 0,
                  color: "#383838",
                }}
              >
                Guided Assurance Composition Workspace
              </Typography>
            </Box>

            {/* Task F — live timestamp, right of title */}
            <Typography
              sx={{
                flexShrink: 0,
                width: "auto",
                maxWidth: { xs: "100%", sm: 448 },
                height: 27,
                m: 0,
                fontFamily: "Lato, sans-serif",
                fontWeight: 400,
                fontStyle: "Regular",
                fontSize: "16px",
                lineHeight: "27px",
                letterSpacing: "0px",
                textAlign: "right",
                verticalAlign: "middle",
                color: "#555555",
                whiteSpace: "nowrap",
              }}
            >
              {lastUpdatedLabel}
            </Typography>
          </Box>

          {/* Task D — description */}
          <Typography
            sx={{
              width: { xs: "100%", md: 1198 },
              maxWidth: "100%",
              height: 19,
              m: 0,
              pl: `${HEADER_TEXT_COLUMN_INSET_PX}px`,
              boxSizing: "border-box",
              fontFamily: "Lato, sans-serif",
              fontWeight: 400,
              fontStyle: "Regular",
              fontSize: "16px",
              lineHeight: "100%",
              letterSpacing: 0,
              color: "#555555",
              whiteSpace: { xs: "normal", md: "nowrap" },
              overflow: { md: "hidden" },
              textOverflow: { md: "ellipsis" },
            }}
          >
            Confirm this AI system is ready before you configure its assurance plan.
          </Typography>

          {/* Canonical + Derived + icon — Figma Frame 1410120209 / 0208 */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "8px",
              width: "fit-content",
              minHeight: 24,
              // Extra space after description (stack gap 8 + 8 = 16px like Figma)
              mt: "8px",
              ml: `${HEADER_TEXT_COLUMN_INSET_PX}px`,
              opacity: 1,
            }}
          >
            {/* Canonical flag */}
            <Box
              component="span"
              sx={{
                ...HEADER_FLAG_BASE_SX,
                minWidth: 73,
                bgcolor: "#F5EEFF",
              }}
            >
              <Box
                component="span"
                sx={{
                  ...HEADER_FLAG_LABEL_SX,
                  minWidth: 57,
                  color: "#7F2CE8",
                }}
              >
                Canonical
              </Box>
            </Box>

            {/* Derived flag */}
            <Box
              component="span"
              sx={{
                ...HEADER_FLAG_BASE_SX,
                minWidth: 62,
                bgcolor: "#BDD6FF",
              }}
            >
              <Box
                component="span"
                sx={{
                  ...HEADER_FLAG_LABEL_SX,
                  minWidth: 46,
                  color: "#0046B8",
                }}
              >
                Derived
              </Box>
            </Box>

            {/* Icon after flags — ChevronLeft.svg + Canonical/Derived info tooltip */}
            <Tooltip
              arrow
              placement="bottom-end"
              enterDelay={120}
              title={
                <Box
                  sx={{
                    width: "100%",
                    fontFamily: "Lato, sans-serif",
                    color: "#E6E6E6",
                  }}
                >
                  <Typography
                    sx={{
                      m: 0,
                      mb: "8px",
                      fontFamily: "Lato, sans-serif",
                      fontWeight: 700,
                      fontStyle: "Bold",
                      fontSize: "12px",
                      lineHeight: "18px",
                      letterSpacing: "0%",
                      verticalAlign: "middle",
                      color: "#E6E6E6",
                    }}
                  >
                    Canonical
                  </Typography>
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      mb: "12px",
                      pl: "18px",
                      fontFamily: "Lato, sans-serif",
                      fontWeight: 400,
                      fontStyle: "Regular",
                      fontSize: "12px",
                      lineHeight: "18px",
                      letterSpacing: "0%",
                      color: "#E6E6E6",
                    }}
                  >
                    <Box component="li">Governed centrally by platform</Box>
                    <Box component="li">Standardized for consistency and audibility</Box>
                    <Box component="li">Can be deactivated</Box>
                    <Box component="li">Cannot be deleted</Box>
                  </Box>

                  <Typography
                    sx={{
                      m: 0,
                      mb: "8px",
                      fontFamily: "Lato, sans-serif",
                      fontWeight: 700,
                      fontStyle: "Bold",
                      fontSize: "12px",
                      lineHeight: "18px",
                      letterSpacing: "0%",
                      verticalAlign: "middle",
                      color: "#E6E6E6",
                    }}
                  >
                    Derived
                  </Typography>
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      mb: "12px",
                      pl: "18px",
                      fontFamily: "Lato, sans-serif",
                      fontWeight: 400,
                      fontStyle: "Regular",
                      fontSize: "12px",
                      lineHeight: "18px",
                      letterSpacing: "0%",
                      color: "#E6E6E6",
                    }}
                  >
                    <Box component="li">Defined and customized by enterprise teams</Box>
                    <Box component="li">Editable and configurable</Box>
                    <Box component="li">Supports organization-specific workflows</Box>
                    <Box component="li">Can be deleted when no longer needed</Box>
                  </Box>

                  <Typography
                    sx={{
                      m: 0,
                      fontFamily: "Lato, sans-serif",
                      fontWeight: 400,
                      fontStyle: "Regular",
                      fontSize: "12px",
                      lineHeight: "18px",
                      letterSpacing: "0%",
                      color: "#E6E6E6",
                    }}
                  >
                    Canonical and Derived item types appear throughout the workflow to indicate
                    governance ownership and editing permissions.
                  </Typography>
                </Box>
              }
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#000000",
                    color: "#E6E6E6",
                    borderRadius: "4px",
                    p: "12px",
                    width: 282,
                    maxWidth: 282,
                    minHeight: "auto",
                    height: "auto",
                    boxSizing: "border-box",
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.28)",
                  },
                },
                arrow: {
                  sx: { color: "#000000" },
                },
              }}
            >
              <Box
                component="span"
                role="img"
                aria-label="Canonical and Derived information"
                sx={{
                  position: "relative",
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  opacity: 1,
                  display: "inline-flex",
                  cursor: "pointer",
                  "& svg": {
                    position: "absolute",
                    top: "2px",
                    left: "2px",
                    width: 20,
                    height: 20,
                    display: "block",
                  },
                  "& path": {
                    fill: "#000000",
                  },
                }}
              >
                <ChevronLeftIcon />
              </Box>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Scroll body — full width fluid (no max-width column) */}
      <Box sx={SCROLL_AREA_SX}>
        <Box sx={{ width: "100%", minWidth: 0 }}>
          <Step1TrustAttributes
            projectId={projectId}
            onWeightsReady={handleStep1ReadyChange}
          />

          <Step2RiskRegister
            projectId={projectId}
            unlocked={step1Ready}
            onRisksReady={handleRisksReadyChange}
            lockReason={
              step1Ready
                ? null
                : "Generate trust attribute weights in Step 1 (total must equal 100%) to unlock the Risk Register"
            }
          />

          <Step3MetricsRegister
            projectId={projectId}
            unlocked={step2Ready}
            risksRevision={risksRevision}
            onMetricsReady={setMetricsReady}
            lockReason={
              step2Ready
                ? null
                : "Generate risks in Step 2 to unlock the Trust Metrics Register"
            }
          />
        </Box>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          height: 56,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "24px",
          bgcolor: "#FFFFFF",
          boxShadow: "0px -1px 8px 0px #2122260A",
        }}
      >
        <Button
          disableElevation
          disableRipple
          onClick={handlePageBack}
          sx={footerGhostBtnSx}
        >
          Back
        </Button>
        <Button
          variant="contained"
          disableElevation
          disableRipple
          startIcon={<RegenerateIcon />}
          disabled={!metricsReady}
          onClick={() => navigate(`/test-strategy/${projectId}`)}
          sx={generateTestStrategyBtnSx}
        >
          Generate Test Strategy
        </Button>
      </Box>
    </Box>
  );
}
